import React from "react";
import { useNavigate } from "react-router-dom";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm, useWatch, type UseFormReturn } from "react-hook-form";
import * as z from "zod";
import { useCreateCampaign, useGetCampaign, useUpdateCampaign, getGetCampaignQueryKey, CampaignBodyStatus, type CampaignBody, type Campaign } from "@/lib/hiring-api";
import { logCreate, logUpdate } from "@/lib/audit-log";
import { useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { Loader2, ChevronDown, ChevronRight, CheckCircle2, Package, Video, DollarSign, Gift, Trophy, Plus, X, Upload, ImageIcon, Link2 } from "lucide-react";
import { useEffect, useState, useRef, useCallback } from "react";

const prizeEntrySchema = z.object({
  place: z.number(),
  amount: z.coerce.number().min(0),
});

const confirmationStepSchema = z.object({
  title: z.string(),
  body: z.string(),
  show_tap_button: z.boolean().optional(),
});

type ConfirmationStep = z.infer<typeof confirmationStepSchema>;

const DEFAULT_CONFIRMATION_STEPS: ConfirmationStep[] = [
  {
    title: "Request Your Sample",
    body: "Tap the button below to request your free product sample through TikTok Shop.",
    show_tap_button: true,
  },
  {
    title: "Create Your Videos",
    body: "Once your sample arrives, create {numVideos} videos featuring {productLabel}.\n\nA few things to keep in mind:\n- Please spread your videos out — don't post them all in one day\n- Let's target having all {numVideos} videos posted within 28 days",
    show_tap_button: false,
  },
  {
    title: "GET PAID",
    body: "Request Your Payment. Payments are processed through Trolley. Once your campaign is complete, you'll receive an email invite to connect your Trolley account (if you haven't already). From there, we process payouts as the campaign is running and at latest, shortly after campaign completion.",
    show_tap_button: false,
  },
];

const DEFAULT_CONFIRMATION_FOOTER = "Questions? Just ask support@brandsmeetcreators.com and our team will get back to you.";

const campaignSchema = z.object({
  brand_name: z.string().min(1, "Brand name is required"),
  product_name: z.string().optional(),
  offer_amount: z.coerce.number().min(1, "Offer amount must be greater than 0"),
  num_videos_per_creator: z.coerce.number().min(1, "Must request at least 1 video"),
  max_spots: z.coerce.number().min(1, "Required"),
  brief_requirements: z.string().optional(),
  tap_link: z.string().url("Must be a valid URL").min(1, "TAP Link URL is required"),
  has_rewards: z.boolean().optional(),
  prize_pool: z.array(prizeEntrySchema).optional(),
  product_id: z.string().optional(),
  campaign_start_date: z.string().optional(),
  campaign_end_date: z.string().optional(),
  additional_info: z.string().optional(),
  status: z.nativeEnum(CampaignBodyStatus).default(CampaignBodyStatus.active),
  accept_headline: z.string().optional(),
  accept_cta_text: z.string().optional(),
  accepted_title: z.string().optional(),
  accepted_message: z.string().optional(),
  confirmation_steps: z.array(confirmationStepSchema).optional(),
  confirmation_footer: z.string().optional(),
  show_bonus_note: z.boolean().optional(),
  bonus_note_text: z.string().optional(),
  commission_text: z.string().optional(),
});

type CampaignFormValues = z.infer<typeof campaignSchema>;

interface CampaignFormProps {
  id?: string;
}

const DEFAULT_PRIZE_POOL = [
  { place: 1, amount: 500 },
  { place: 2, amount: 300 },
  { place: 3, amount: 200 },
];

const PLACE_LABELS: Record<number, string> = {
  1: "1st Place",
  2: "2nd Place",
  3: "3rd Place",
  4: "4th Place",
  5: "5th Place",
  6: "6th Place",
  7: "7th Place",
  8: "8th Place",
  9: "9th Place",
  10: "10th Place",
};

const PLACE_COLORS: Record<number, string> = {
  1: "text-yellow-600",
  2: "text-slate-500",
  3: "text-amber-700",
};

function InlineInput({ value, onChange, placeholder, className }: {
  value: string;
  onChange: (v: string) => void;
  placeholder: string;
  className?: string;
}) {
  return (
    <input
      type="text"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      className={`w-full bg-transparent border-0 border-b border-transparent hover:border-dashed hover:border-gray-300 focus:border-solid focus:border-[#FB3AAE] focus:outline-none transition-colors ${className || ""}`}
    />
  );
}

function InlineTextarea({ value, onChange, placeholder, className }: {
  value: string;
  onChange: (v: string) => void;
  placeholder: string;
  className?: string;
}) {
  const ref = useRef<HTMLTextAreaElement>(null);

  const autoResize = useCallback(() => {
    if (ref.current) {
      ref.current.style.height = "auto";
      ref.current.style.height = ref.current.scrollHeight + "px";
    }
  }, []);

  useEffect(() => { autoResize(); }, [value, autoResize]);

  return (
    <textarea
      ref={ref}
      value={value}
      onChange={(e) => { onChange(e.target.value); autoResize(); }}
      placeholder={placeholder}
      rows={1}
      className={`w-full bg-transparent border-0 border-b border-transparent hover:border-dashed hover:border-gray-300 focus:border-solid focus:border-[#FB3AAE] focus:outline-none resize-none overflow-hidden transition-colors p-0 ${className || ""}`}
    />
  );
}

function renderWithLinks(text: string): React.ReactNode[] {
  const pattern = /\[([^\]]+)\]\((https?:\/\/[^)]+)\)/g;
  const parts: React.ReactNode[] = [];
  let last = 0;
  let m: RegExpExecArray | null;
  while ((m = pattern.exec(text)) !== null) {
    if (m.index > last) parts.push(text.slice(last, m.index));
    parts.push(
      <a key={m.index} href={m[2]} target="_blank" rel="noopener noreferrer" className="text-[#FB3AAE] underline break-all">
        {m[1]}
      </a>
    );
    last = m.index + m[0].length;
  }
  if (last < text.length) parts.push(text.slice(last));
  return parts;
}

function renderWithLinksAndNewlines(text: string): React.ReactNode {
  return text.split("\n").map((line, i) => (
    <React.Fragment key={i}>
      {i > 0 && <br />}
      {renderWithLinks(line)}
    </React.Fragment>
  ));
}

function ContentGuidelinesField({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const [cursorPos, setCursorPos] = useState<number | null>(null);
  const [showLinkForm, setShowLinkForm] = useState(false);
  const [linkText, setLinkText] = useState("");
  const [linkUrl, setLinkUrl] = useState("");
  const [urlError, setUrlError] = useState("");

  const autoResize = useCallback(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = "auto";
      textareaRef.current.style.height = textareaRef.current.scrollHeight + "px";
    }
  }, []);

  useEffect(() => { autoResize(); }, [value, autoResize]);

  function trackCursor() {
    setCursorPos(textareaRef.current?.selectionStart ?? null);
  }

  function normalizeUrl(url: string): string {
    if (/^https?:\/\//i.test(url)) return url;
    return "https://" + url;
  }

  function isValidUrl(url: string): boolean {
    try {
      const parsed = new URL(normalizeUrl(url));
      return !!parsed.hostname && parsed.hostname.includes(".");
    } catch {
      return false;
    }
  }

  function handleUrlChange(raw: string) {
    setLinkUrl(raw);
    if (raw && !isValidUrl(raw)) {
      setUrlError("Enter a valid URL e.g. tiktok.com or https://tiktok.com");
    } else {
      setUrlError("");
    }
  }

  function insertLink() {
    if (!linkUrl || !isValidUrl(linkUrl)) {
      setUrlError("Enter a valid URL e.g. tiktok.com or https://tiktok.com");
      return;
    }
    const normalized = normalizeUrl(linkUrl);
    const display = linkText.trim() || normalized;
    const markdown = `[${display}](${normalized})`;
    const pos = cursorPos ?? value.length;
    const newValue = value.slice(0, pos) + markdown + value.slice(pos);
    onChange(newValue);
    setShowLinkForm(false);
    setLinkText("");
    setLinkUrl("");
    setUrlError("");
    setTimeout(() => {
      if (textareaRef.current) {
        const newPos = pos + markdown.length;
        textareaRef.current.focus();
        textareaRef.current.setSelectionRange(newPos, newPos);
      }
    }, 0);
  }

  return (
    <div>
      <textarea
        ref={textareaRef}
        value={value}
        onChange={(e) => { onChange(e.target.value); autoResize(); trackCursor(); }}
        onSelect={trackCursor}
        onClick={trackCursor}
        onKeyUp={trackCursor}
        placeholder="Enter your content guidelines here... What should the creator know about the campaign?"
        rows={1}
        className="w-full bg-transparent border-0 border-b border-transparent hover:border-dashed hover:border-gray-300 focus:border-solid focus:border-[#FB3AAE] focus:outline-none resize-none overflow-hidden transition-colors p-0 text-xs text-gray-700 leading-relaxed"
      />
      <div className="flex justify-end mt-1">
        <button
          type="button"
          onClick={() => setShowLinkForm(!showLinkForm)}
          className="flex items-center gap-1 text-[10px] text-[#FB3AAE] hover:text-[#E0309C] transition-colors"
        >
          <Link2 className="h-3 w-3" /> Add Link
        </button>
      </div>
      {showLinkForm && (
        <div className="mt-1 p-2 bg-slate-50 border border-slate-200 rounded-lg space-y-1.5">
          <input
            type="text"
            placeholder="Display text (optional)"
            value={linkText}
            onChange={(e) => setLinkText(e.target.value)}
            className="w-full text-xs border border-slate-200 rounded px-2 py-1 focus:outline-none focus:border-[#FB3AAE]"
          />
          <div className="space-y-0.5">
            <input
              type="text"
              placeholder="https://..."
              value={linkUrl}
              onChange={(e) => handleUrlChange(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && insertLink()}
              className={`w-full text-xs border rounded px-2 py-1 focus:outline-none ${urlError ? "border-red-400 focus:border-red-500" : "border-slate-200 focus:border-[#FB3AAE]"}`}
            />
            {urlError && <p className="text-[10px] text-red-500">{urlError}</p>}
          </div>
          <div className="flex gap-1.5 justify-end">
            <button type="button" onClick={() => { setShowLinkForm(false); setLinkText(""); setLinkUrl(""); setUrlError(""); }} className="text-[10px] text-gray-500 hover:text-gray-700 px-2 py-0.5">
              Cancel
            </button>
            <button
              type="button"
              onClick={insertLink}
              disabled={!linkUrl || !!urlError}
              className="text-[10px] bg-[#FB3AAE] text-white rounded px-2 py-0.5 disabled:opacity-50 hover:bg-[#E0309C] transition-colors"
            >
              Insert
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function PrizePoolEditor({ prizes, onChange }: { prizes: { place: number; amount: number }[]; onChange: (p: { place: number; amount: number }[]) => void }) {
  const updateAmount = (index: number, amount: number) => {
    const updated = [...prizes];
    updated[index] = { ...updated[index], amount };
    onChange(updated);
  };

  const addPlace = () => {
    if (prizes.length >= 10) return;
    const nextPlace = prizes.length + 1;
    onChange([...prizes, { place: nextPlace, amount: 100 }]);
  };

  const removePlace = (index: number) => {
    if (prizes.length <= 1) return;
    const updated = prizes.filter((_, i) => i !== index).map((p, i) => ({ ...p, place: i + 1 }));
    onChange(updated);
  };

  return (
    <div className="space-y-3">
      <div className="space-y-2">
        {prizes.map((prize, index) => (
          <div key={index} className="flex items-center gap-3">
            <div className="flex items-center gap-1.5 w-24 shrink-0">
              <Trophy className={`h-3.5 w-3.5 ${PLACE_COLORS[prize.place] || "text-gray-400"}`} />
              <span className="text-sm font-medium text-gray-700">{PLACE_LABELS[prize.place]}</span>
            </div>
            <div className="flex items-center gap-1 flex-1">
              <span className="text-sm text-gray-500">$</span>
              <Input
                type="number"
                value={prize.amount}
                onChange={(e) => updateAmount(index, Number(e.target.value))}
                className="h-8 text-sm"
                min={0}
              />
            </div>
            {prizes.length > 1 && (
              <button type="button" onClick={() => removePlace(index)} className="text-gray-400 hover:text-red-500 transition-colors">
                <X className="h-4 w-4" />
              </button>
            )}
          </div>
        ))}
      </div>
      {prizes.length < 10 && (
        <button
          type="button"
          onClick={addPlace}
          className="flex items-center gap-1.5 text-xs font-medium text-[#FB3AAE] hover:text-[#E0309C] transition-colors"
        >
          <Plus className="h-3.5 w-3.5" /> Add Place
        </button>
      )}
    </div>
  );
}

export function PrizePoolDisplay({ prizes, compact }: { prizes: { place: number; amount: number }[]; compact?: boolean }) {
  if (!prizes || prizes.length === 0) return null;

  const medalEmojis: Record<number, string> = { 1: "\uD83E\uDD47", 2: "\uD83E\uDD48", 3: "\uD83E\uDD49" };

  if (compact) {
    const top2 = prizes.slice(0, 2);
    return (
      <div className="flex items-center gap-3">
        {top2.map((p) => (
          <span key={p.place} className="text-xs font-semibold">
            {medalEmojis[p.place] || `#${p.place}`} ${p.amount.toLocaleString()}
          </span>
        ))}
        {prizes.length > 2 && <span className="text-xs text-gray-500">+ {prizes.length - 2} more</span>}
      </div>
    );
  }

  return (
    <div className="bg-gradient-to-br from-yellow-50 to-amber-50 rounded-lg p-4 border border-yellow-200">
      <div className="flex items-center gap-1.5 mb-3">
        <Trophy className="h-4 w-4 text-yellow-600" />
        <p className="text-xs font-bold text-yellow-800 uppercase tracking-wider">Rewards Prize Pool</p>
      </div>
      <div className="space-y-2">
        {prizes.map((prize) => (
          <div key={prize.place} className={`flex items-center justify-between ${prize.place <= 2 ? "bg-white/80 rounded-md p-2 shadow-sm" : "px-2 py-1"}`}>
            <span className="text-sm font-medium text-gray-700 flex items-center gap-1.5">
              {medalEmojis[prize.place] ? (
                <span className="text-base">{medalEmojis[prize.place]}</span>
              ) : (
                <span className="text-xs text-gray-400 w-5 text-center">#{prize.place}</span>
              )}
              {PLACE_LABELS[prize.place]}
            </span>
            <span className={`text-sm font-bold ${prize.place === 1 ? "text-yellow-700 text-base" : prize.place === 2 ? "text-slate-600" : "text-gray-600"}`}>
              ${prize.amount.toLocaleString()}
            </span>
          </div>
        ))}
        <div className="border-t border-yellow-200 pt-2 mt-2 flex justify-between px-2">
          <span className="text-xs font-semibold text-yellow-800">Total Prize Pool</span>
          <span className="text-sm font-bold text-yellow-800">${prizes.reduce((s, p) => s + p.amount, 0).toLocaleString()}</span>
        </div>
      </div>
    </div>
  );
}

function OfferPageEditor({ form, values }: { form: UseFormReturn<CampaignFormValues>; values: CampaignFormValues }) {
  const brandName = values.brand_name || "Brand Name";
  const productName = values.product_name;
  const offerAmount = values.offer_amount || 0;
  const numVideos = values.num_videos_per_creator || 1;
  const hasRewards = values.has_rewards || false;
  const prizePool = values.prize_pool || [];
  const maxSpots = values.max_spots;
  const guaranteed = offerAmount * numVideos;
  const firstPlaceAmount = prizePool.length > 0 ? prizePool[0].amount : 0;
  const totalPrize = prizePool.reduce((s: number, p: { place: number; amount: number }) => s + p.amount, 0);
  const showBonus = values.show_bonus_note || false;

  return (
    <div className="bg-slate-100 rounded-lg p-6">
      <div className="max-w-md mx-auto space-y-3">
        <div className="text-center">
          <img src="/bmc-logo.png" alt="BMC" className="h-8 mx-auto" />
        </div>
        <div className="bg-white rounded-xl shadow-sm overflow-hidden">
          <div className="p-6">
            <InlineInput
              value={values.accept_headline || ""}
              onChange={(v) => form.setValue("accept_headline", v)}
              placeholder="You've been invited to create for"
              className="text-sm text-gray-500 mb-1"
            />
            <p className="text-xl font-black text-gray-900 uppercase mb-1">
              {brandName}
              {productName && (
                <span className="text-sm font-medium text-gray-500 ml-2 normal-case">
                  — {productName}
                </span>
              )}
            </p>
            <p className="text-[11px] text-gray-400 mb-5">This is a TikTok Shop affiliate campaign.</p>

            {values.tap_link && (
              <div className="mb-4 p-2.5 bg-slate-50 rounded-lg border border-slate-200">
                <p className="text-[10px] font-semibold text-gray-800 mb-1">👀 Want to check out the product first?</p>
                <p className="text-[10px] text-[#FB3AAE] font-medium mb-1">View Product on TikTok →</p>
                <p className="text-[9px] text-gray-400">⚠️ Opens in TikTok app. Come back here to accept.</p>
                <p className="text-[9px] text-gray-400">Don't request a sample yet — accept the deal first.</p>
              </div>
            )}

            {hasRewards && totalPrize > 0 ? (
              <div className="mb-5">
                <div className="grid grid-cols-2 gap-2 mb-2">
                  <div className="bg-[#FB3AAE]/5 border border-[#FB3AAE]/20 rounded-xl p-3 text-center">
                    <p className="text-xl">💰</p>
                    <p className="text-lg font-bold text-[#FB3AAE]">${guaranteed.toLocaleString()}</p>
                    <p className="text-[10px] text-gray-500 font-medium">guaranteed</p>
                  </div>
                  <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-3 text-center">
                    <p className="text-xl">🏆</p>
                    <p className="text-lg font-bold text-yellow-700">${totalPrize.toLocaleString()}</p>
                    <p className="text-[10px] text-gray-500 font-medium">in prizes</p>
                    {maxSpots && (
                      <p className="text-[9px] text-yellow-600 mt-0.5">~{maxSpots} creators</p>
                    )}
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-1.5">
                  <div className="bg-slate-50 rounded-lg p-2 text-center">
                    <p className="text-sm font-bold text-gray-700">${offerAmount}</p>
                    <p className="text-[10px] text-gray-500">per video</p>
                  </div>
                  <div className="bg-slate-50 rounded-lg p-2 text-center">
                    <p className="text-sm font-bold text-gray-700">{numVideos}</p>
                    <p className="text-[10px] text-gray-500">videos</p>
                  </div>
                </div>
              </div>
            ) : (
              <div className="grid grid-cols-3 gap-2.5 mb-5">
                <div className="bg-slate-50 rounded-lg p-2.5 text-center">
                  <DollarSign className="h-4 w-4 text-green-600 mx-auto mb-0.5" />
                  <p className="text-base font-bold text-green-700">${offerAmount}</p>
                  <p className="text-[10px] text-gray-500">per video</p>
                </div>
                <div className="bg-slate-50 rounded-lg p-2.5 text-center">
                  <Video className="h-4 w-4 text-blue-600 mx-auto mb-0.5" />
                  <p className="text-base font-bold text-blue-700">{numVideos}</p>
                  <p className="text-[10px] text-gray-500">videos</p>
                </div>
                <div className="bg-slate-50 rounded-lg p-2.5 text-center">
                  <DollarSign className="h-4 w-4 text-[#FB3AAE] mx-auto mb-0.5" />
                  <p className="text-base font-bold text-[#FB3AAE]">${guaranteed}</p>
                  <p className="text-[10px] text-gray-500">guaranteed</p>
                </div>
              </div>
            )}

            <div className="mb-4">
              <p className="text-[10px] font-bold text-gray-900 uppercase tracking-wider mb-1">📋 Content Guidelines</p>
              <ContentGuidelinesField
                value={values.brief_requirements || ""}
                onChange={(v) => form.setValue("brief_requirements", v)}
              />
            </div>

            {values.additional_info !== undefined && (
              <div className="mb-4">
                <InlineTextarea
                  value={values.additional_info || ""}
                  onChange={(v) => form.setValue("additional_info", v)}
                  placeholder="Any additional details for the creator... (leave empty to hide this section)"
                  className="text-xs text-gray-600 leading-relaxed"
                />
              </div>
            )}

            <hr className="border-slate-200 mb-4" />

            <div className="mb-4">
              <p className="text-[10px] font-bold text-gray-900 uppercase tracking-wider mb-1">{"\uD83D\uDCB8"} TikTok Shop Commission</p>
              <InlineTextarea
                value={values.commission_text || ""}
                onChange={(v) => form.setValue("commission_text", v)}
                placeholder="Describe the TikTok Shop commission details..."
                className="text-xs text-gray-500 leading-relaxed"
              />
            </div>

            {hasRewards && prizePool.length > 0 && (
              <>
                <hr className="border-slate-200 mb-4" />
                <div className="mb-4">
                  <p className="text-[10px] font-bold text-gray-900 uppercase tracking-wider mb-2">{"\uD83C\uDFC6"} Bonus: Rewards Campaign</p>
                  <p className="text-xs text-gray-500 leading-relaxed mb-1">
                    On top of your guaranteed ${guaranteed.toLocaleString()}, compete for cash prizes based on your total GMV for {brandName} by the end of the campaign.
                  </p>
                  {maxSpots && (
                    <p className="text-xs text-gray-500 leading-relaxed mb-3">
                      We're targeting around {maxSpots} creators for this campaign. This is a private rewards campaign only applicable to selected BMC creators — your odds of winning are real with only {maxSpots} participants.
                    </p>
                  )}
                  <div className="space-y-1 mb-2">
                    {prizePool.slice(0, 5).map((prize: { place: number; amount: number }) => {
                      const medals: Record<number, string> = { 1: "\uD83E\uDD47", 2: "\uD83E\uDD48", 3: "\uD83E\uDD49" };
                      const labels: Record<number, string> = { 1: "1st Place", 2: "2nd Place", 3: "3rd Place", 4: "4th Place", 5: "5th Place" };
                      return (
                        <div key={prize.place} className="flex justify-between text-xs">
                          <span className="text-gray-700">{medals[prize.place] || `#${prize.place}`} {labels[prize.place] || `Place #${prize.place}`}</span>
                          <span className="font-bold text-gray-900">${prize.amount.toLocaleString()}</span>
                        </div>
                      );
                    })}
                  </div>
                  <p className="text-xs text-gray-400 mb-2">Total prize pool: ${totalPrize.toLocaleString()}</p>
                </div>

                <hr className="border-slate-200 mb-4" />
                <div className="mb-5">
                  <p className="text-[10px] font-bold text-gray-900 uppercase tracking-wider mb-2">Your Total Earning Potential</p>
                  <div className="space-y-1 text-xs">
                    <div className="flex justify-between">
                      <span className="text-gray-500">Guaranteed pay:</span>
                      <span className="font-medium">${guaranteed.toLocaleString()}</span>
                    </div>
                    {firstPlaceAmount > 0 && (
                      <div className="flex justify-between">
                        <span className="text-gray-500">Prize pool:</span>
                        <span className="font-medium">up to ${firstPlaceAmount.toLocaleString()}</span>
                      </div>
                    )}
                    <div className="flex justify-between">
                      <span className="text-gray-500">TikTok Shop commission:</span>
                      <span className="font-medium">uncapped</span>
                    </div>
                    <div className="flex justify-between font-bold border-t border-slate-200 pt-1 mt-1">
                      <span className="text-gray-700">Potential total:</span>
                      <span className="text-[#FB3AAE]">${(guaranteed + firstPlaceAmount).toLocaleString()}+</span>
                    </div>
                  </div>
                </div>
              </>
            )}

            <hr className="border-slate-200 mb-4" />

            <div className="w-full rounded-md bg-[#FB3AAE] overflow-hidden mb-2">
              <InlineInput
                value={values.accept_cta_text || ""}
                onChange={(v) => form.setValue("accept_cta_text", v)}
                placeholder="I'M IN — ACCEPT THIS DEAL"
                className="text-sm font-bold text-white text-center h-10 placeholder:text-white/70 hover:border-white/50 focus:border-white"
              />
            </div>
            <p className="text-[10px] text-center text-gray-400">
              This link is unique to you. By accepting you agree to create content per the guidelines above.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

function OfferPagePreview({ values }: { values: CampaignFormValues }) {
  const brandName = values.brand_name || "Brand Name";
  const productName = values.product_name;
  const offerAmount = values.offer_amount || 0;
  const numVideos = values.num_videos_per_creator || 1;
  const hasRewards = values.has_rewards || false;
  const prizePool = values.prize_pool || [];
  const maxSpots = values.max_spots;
  const guaranteed = offerAmount * numVideos;
  const firstPlaceAmount = prizePool.length > 0 ? prizePool[0].amount : 0;
  const totalPrize = prizePool.reduce((s: number, p: { place: number; amount: number }) => s + p.amount, 0);
  const showBonus = values.show_bonus_note || false;
  const acceptHeadline = values.accept_headline || "You've been invited to create for";
  const ctaText = values.accept_cta_text || "I'M IN — ACCEPT THIS DEAL";

  return (
    <div className="bg-slate-100 rounded-lg p-6">
      <div className="max-w-md mx-auto space-y-3">
        <div className="text-center">
          <img src="/bmc-logo.png" alt="BMC" className="h-8 mx-auto" />
        </div>
        <div className="bg-white rounded-xl shadow-sm overflow-hidden">
          <div className="p-6">
            <p className="text-sm text-gray-500 mb-1">{acceptHeadline}</p>
            <p className="text-xl font-black text-gray-900 uppercase mb-1">
              {brandName}
              {productName && (
                <span className="text-sm font-medium text-gray-500 ml-2 normal-case">— {productName}</span>
              )}
            </p>
            <p className="text-[11px] text-gray-400 mb-4">This is a TikTok Shop affiliate campaign.</p>

            {values.tap_link && (
              <div className="mb-4 p-2.5 bg-slate-50 rounded-lg border border-slate-200">
                <p className="text-[10px] font-semibold text-gray-800 mb-1">👀 Want to check out the product first?</p>
                <p className="text-[10px] text-[#FB3AAE] font-medium mb-1">View Product on TikTok →</p>
                <p className="text-[9px] text-gray-400">⚠️ Opens in TikTok app. Come back here to accept.</p>
                <p className="text-[9px] text-gray-400">Don't request a sample yet — accept the deal first.</p>
              </div>
            )}

            {hasRewards && totalPrize > 0 ? (
              <div className="mb-5">
                <div className="grid grid-cols-2 gap-2 mb-2">
                  <div className="bg-[#FB3AAE]/5 border border-[#FB3AAE]/20 rounded-xl p-3 text-center">
                    <p className="text-xl">💰</p>
                    <p className="text-lg font-bold text-[#FB3AAE]">${guaranteed.toLocaleString()}</p>
                    <p className="text-[10px] text-gray-500 font-medium">guaranteed</p>
                  </div>
                  <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-3 text-center">
                    <p className="text-xl">🏆</p>
                    <p className="text-lg font-bold text-yellow-700">${totalPrize.toLocaleString()}</p>
                    <p className="text-[10px] text-gray-500 font-medium">in prizes</p>
                    {maxSpots && (
                      <p className="text-[9px] text-yellow-600 mt-0.5">~{maxSpots} creators</p>
                    )}
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-1.5">
                  <div className="bg-slate-50 rounded-lg p-2 text-center">
                    <p className="text-sm font-bold text-gray-700">${offerAmount}</p>
                    <p className="text-[10px] text-gray-500">per video</p>
                  </div>
                  <div className="bg-slate-50 rounded-lg p-2 text-center">
                    <p className="text-sm font-bold text-gray-700">{numVideos}</p>
                    <p className="text-[10px] text-gray-500">videos</p>
                  </div>
                </div>
              </div>
            ) : (
              <div className="grid grid-cols-3 gap-2.5 mb-5">
                <div className="bg-slate-50 rounded-lg p-2.5 text-center">
                  <DollarSign className="h-4 w-4 text-green-600 mx-auto mb-0.5" />
                  <p className="text-base font-bold text-green-700">${offerAmount}</p>
                  <p className="text-[10px] text-gray-500">per video</p>
                </div>
                <div className="bg-slate-50 rounded-lg p-2.5 text-center">
                  <Video className="h-4 w-4 text-blue-600 mx-auto mb-0.5" />
                  <p className="text-base font-bold text-blue-700">{numVideos}</p>
                  <p className="text-[10px] text-gray-500">videos</p>
                </div>
                <div className="bg-slate-50 rounded-lg p-2.5 text-center">
                  <DollarSign className="h-4 w-4 text-[#FB3AAE] mx-auto mb-0.5" />
                  <p className="text-base font-bold text-[#FB3AAE]">${guaranteed}</p>
                  <p className="text-[10px] text-gray-500">guaranteed</p>
                </div>
              </div>
            )}

            {values.brief_requirements && (
              <div className="mb-4">
                <p className="text-[10px] font-bold text-gray-900 uppercase tracking-wider mb-1">📋 Content Guidelines</p>
                <p className="text-xs text-gray-700 leading-relaxed">{renderWithLinksAndNewlines(values.brief_requirements)}</p>
              </div>
            )}

            {values.additional_info && (
              <p className="text-xs text-gray-600 leading-relaxed mb-4 whitespace-pre-wrap">{values.additional_info}</p>
            )}

            <hr className="border-slate-200 mb-4" />

            {values.commission_text && (
              <div className="mb-4">
                <p className="text-[10px] font-bold text-gray-900 uppercase tracking-wider mb-1">💸 TikTok Shop Commission</p>
                <p className="text-xs text-gray-500 leading-relaxed">{values.commission_text}</p>
              </div>
            )}

            {hasRewards && prizePool.length > 0 && (
              <>
                <hr className="border-slate-200 mb-4" />
                <div className="mb-4">
                  <p className="text-[10px] font-bold text-gray-900 uppercase tracking-wider mb-2">🏆 Bonus: Rewards Campaign</p>
                  <p className="text-xs text-gray-500 leading-relaxed mb-1">
                    On top of your guaranteed ${guaranteed.toLocaleString()}, compete for cash prizes based on your total GMV for {brandName} by the end of the campaign.
                  </p>
                  {maxSpots && (
                    <p className="text-xs text-gray-500 leading-relaxed mb-3">We're targeting around {maxSpots} creators for this campaign. This is a private rewards campaign only applicable to selected BMC creators — your odds of winning are real with only {maxSpots} participants.</p>
                  )}
                  <div className="space-y-1 mb-2">
                    {prizePool.slice(0, 5).map((prize: { place: number; amount: number }) => {
                      const medals: Record<number, string> = { 1: "\uD83E\uDD47", 2: "\uD83E\uDD48", 3: "\uD83E\uDD49" };
                      const labels: Record<number, string> = { 1: "1st Place", 2: "2nd Place", 3: "3rd Place", 4: "4th Place", 5: "5th Place" };
                      return (
                        <div key={prize.place} className="flex justify-between text-xs">
                          <span className="text-gray-700">{medals[prize.place] || `#${prize.place}`} {labels[prize.place] || `Place #${prize.place}`}</span>
                          <span className="font-bold text-gray-900">${prize.amount.toLocaleString()}</span>
                        </div>
                      );
                    })}
                  </div>
                  <p className="text-xs text-gray-400 mb-2">Total prize pool: ${totalPrize.toLocaleString()}</p>
                  {showBonus && values.bonus_note_text && (
                    <p className="text-xs text-gray-600 leading-relaxed mt-2">{values.bonus_note_text}</p>
                  )}
                </div>

                <hr className="border-slate-200 mb-4" />
                <div className="mb-5">
                  <p className="text-[10px] font-bold text-gray-900 uppercase tracking-wider mb-2">Your Total Earning Potential</p>
                  <div className="space-y-1 text-xs">
                    <div className="flex justify-between">
                      <span className="text-gray-500">Guaranteed pay:</span>
                      <span className="font-medium">${guaranteed.toLocaleString()}</span>
                    </div>
                    {firstPlaceAmount > 0 && (
                      <div className="flex justify-between">
                        <span className="text-gray-500">Prize pool:</span>
                        <span className="font-medium">up to ${firstPlaceAmount.toLocaleString()}</span>
                      </div>
                    )}
                    <div className="flex justify-between">
                      <span className="text-gray-500">TikTok Shop commission:</span>
                      <span className="font-medium">uncapped</span>
                    </div>
                    <div className="flex justify-between font-bold border-t border-slate-200 pt-1 mt-1">
                      <span className="text-gray-700">Potential total:</span>
                      <span className="text-[#FB3AAE]">${(guaranteed + firstPlaceAmount).toLocaleString()}+</span>
                    </div>
                  </div>
                </div>
              </>
            )}

            <hr className="border-slate-200 mb-4" />
            <div className="w-full rounded-md bg-[#FB3AAE] px-4 py-2.5 text-center mb-2">
              <span className="text-sm font-bold text-white">{ctaText || "I'M IN — ACCEPT THIS DEAL"}</span>
            </div>
            <p className="text-[10px] text-center text-gray-400">
              This link is unique to you. By accepting you agree to create content per the guidelines above.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

function ConfirmationPageEditor({ form, values }: { form: UseFormReturn<CampaignFormValues>; values: CampaignFormValues }) {
  const brandName = values.brand_name || "Brand Name";
  const tapLink = values.tap_link;
  const guaranteed = (values.offer_amount || 0) * (values.num_videos_per_creator || 0);
  const hasRewards = values.has_rewards ?? false;
  const prizePool: { place: number; amount: number }[] = (values.prize_pool || []) as unknown as { place: number; amount: number }[];
  const firstPlaceAmount = prizePool.length > 0 ? prizePool[0].amount : 0;

  const steps: ConfirmationStep[] = values.confirmation_steps && values.confirmation_steps.length > 0
    ? values.confirmation_steps
    : DEFAULT_CONFIRMATION_STEPS;

  const hasTapStep = steps.some((s) => s.show_tap_button);
  const firstTapStepIndex = steps.findIndex((s) => s.show_tap_button);

  const updateStep = (index: number, patch: Partial<ConfirmationStep>) => {
    const updated = steps.map((s, i) => i === index ? { ...s, ...patch } : s);
    form.setValue("confirmation_steps", updated);
  };

  const removeStep = (index: number) => {
    if (steps.length <= 1) return;
    form.setValue("confirmation_steps", steps.filter((_, i) => i !== index));
  };

  const addStep = () => {
    form.setValue("confirmation_steps", [...steps, { title: "New Step", body: "", show_tap_button: false }]);
  };

  return (
    <div className="bg-slate-100 rounded-lg p-6">
      <div className="max-w-md mx-auto space-y-3">
        <div className="text-center">
          <img src="/bmc-logo.png" alt="BMC" className="h-8 mx-auto" />
        </div>
        <div className="bg-white rounded-xl shadow-sm overflow-hidden">
          <div className="p-6">
            <div className="text-center mb-6">
              <div className="flex justify-center mb-3">
                <div className="rounded-full bg-green-100 p-3">
                  <CheckCircle2 className="h-8 w-8 text-green-600" />
                </div>
              </div>
              <InlineInput
                value={values.accepted_title || ""}
                onChange={(v) => form.setValue("accepted_title", v)}
                placeholder="You're in!"
                className="text-xl font-bold text-gray-900 text-center mb-1"
              />
              <InlineTextarea
                value={values.accepted_message || ""}
                onChange={(v) => form.setValue("accepted_message", v)}
                placeholder={`You've accepted the offer with {brand}. Here's everything you need to get started.`}
                className="text-xs text-gray-500 text-center"
              />
              <p className="text-[10px] text-muted-foreground mt-1">Use {"{brand}"} → {brandName}</p>
            </div>

            {hasTapStep && tapLink && (
              <div className="mb-4 bg-[#FB3AAE]/8 border border-[#FB3AAE]/25 rounded-xl px-3 py-2.5 flex items-start gap-2.5">
                <span className="text-base mt-0.5">👇</span>
                <div>
                  <p className="text-xs font-bold text-gray-900">You're not done yet!</p>
                  <p className="text-[10px] text-gray-600 mt-0.5">Complete Step {firstTapStepIndex + 1} below to request your free product sample before you're all set.</p>
                </div>
              </div>
            )}

            {hasTapStep && !tapLink && (
              <p className="text-[10px] text-muted-foreground italic mb-4 text-center">Set a TAP Link URL above to show the sample request button.</p>
            )}

            {steps.map((step, index) => (
              <div key={index}>
                <hr className="border-slate-200 mb-4" />
                <div className="mb-4 group relative">
                  <div className="flex items-start justify-between mb-2 gap-2">
                    <div className="flex items-center gap-1.5 flex-1 min-w-0">
                      <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wider shrink-0">Step {index + 1} —</span>
                      <InlineInput
                        value={step.title}
                        onChange={(v) => updateStep(index, { title: v })}
                        placeholder="Step title"
                        className="text-[10px] font-bold text-gray-900 uppercase tracking-wider"
                      />
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      {index === 0 && (
                        <label className="flex items-center gap-1 cursor-pointer text-[10px] text-gray-400 hover:text-gray-600 whitespace-nowrap">
                          <input
                            type="checkbox"
                            checked={!!step.show_tap_button}
                            onChange={(e) => updateStep(index, { show_tap_button: e.target.checked })}
                            className="w-3 h-3 accent-[#FB3AAE]"
                          />
                          TAP btn
                        </label>
                      )}
                      {/* {steps.length > 1 && (
                        <button
                          type="button"
                          onClick={() => removeStep(index)}
                          className="text-gray-300 hover:text-red-500 transition-colors"
                        >
                          <X className="h-3.5 w-3.5" />
                        </button>
                      )} */}
                    </div>
                  </div>
                  <InlineTextarea
                    value={step.body}
                    onChange={(v) => updateStep(index, { body: v })}
                    placeholder="Step description... Use {numVideos}, {productLabel}, {brand} as placeholders."
                    className="text-xs text-gray-600 leading-relaxed"
                  />
                  {step.show_tap_button && (
                    <div className="mt-2 w-full flex items-center justify-center text-sm font-bold text-white bg-[#FB3AAE] rounded-xl px-3 py-3 shadow-md shadow-pink-200">
                      Request Sample on TikTok →
                    </div>
                  )}
                </div>
              </div>
            ))}

            <div className="mt-2 mb-4">
              <button
                type="button"
                onClick={addStep}
                className="flex items-center gap-1.5 text-xs font-medium text-[#FB3AAE] hover:text-[#E0309C] transition-colors"
              >
                <Plus className="h-3.5 w-3.5" /> Add Step
              </button>
            </div>

            <hr className="border-slate-200 mb-4" />
            <div className="mb-4">
              <p className="text-[10px] font-bold text-gray-900 uppercase tracking-wider mb-2">Your Offer Recap</p>
              <div className="space-y-1.5 text-xs">
                <div className="flex justify-between">
                  <span className="text-gray-500">Guaranteed pay:</span>
                  <span className="font-medium text-gray-900">${guaranteed.toLocaleString()} (${values.offer_amount}/vid × {values.num_videos_per_creator || 0} videos)</span>
                </div>
                {hasRewards && firstPlaceAmount > 0 && (
                  <div className="flex justify-between">
                    <span className="text-gray-500">Prize pool:</span>
                    <span className="font-medium text-gray-900">up to ${firstPlaceAmount.toLocaleString()}</span>
                  </div>
                )}
                <div className="flex justify-between">
                  <span className="text-gray-500">TikTok Shop commission:</span>
                  <span className="font-medium text-gray-900">uncapped</span>
                </div>
                <div className="flex justify-between font-bold border-t border-slate-200 pt-1.5 mt-0.5">
                  <span className="text-gray-700">Potential total:</span>
                  <span className="text-[#FB3AAE]">${(guaranteed + (hasRewards ? firstPlaceAmount : 0)).toLocaleString()}+</span>
                </div>
              </div>
            </div>

            <hr className="border-slate-200 mb-4" />
            <InlineTextarea
              value={values.confirmation_footer || ""}
              onChange={(v) => form.setValue("confirmation_footer", v)}
              placeholder={DEFAULT_CONFIRMATION_FOOTER}
              className="text-xs text-center text-gray-400"
            />
          </div>
        </div>
      </div>
    </div>
  );
}

function ConfirmationPagePreview({ values }: { values: CampaignFormValues }) {
  const brandName = values.brand_name || "Brand Name";
  const tapLink = values.tap_link;
  const acceptedTitle = values.accepted_title || "You're in!";
  const acceptedMessage = (values.accepted_message || "You've accepted the offer with {brand}. Here's everything you need to get started.").replace(/\{brand\}/gi, brandName);
  const guaranteed = (values.offer_amount || 0) * (values.num_videos_per_creator || 0);
  const hasRewards = values.has_rewards ?? false;
  const prizePool: { place: number; amount: number }[] = (values.prize_pool || []) as unknown as { place: number; amount: number }[];
  const firstPlaceAmount = prizePool.length > 0 ? prizePool[0].amount : 0;
  const productLabel = values.product_name || brandName;
  const numVideos = values.num_videos_per_creator || 10;

  const steps: ConfirmationStep[] = values.confirmation_steps && values.confirmation_steps.length > 0
    ? values.confirmation_steps
    : DEFAULT_CONFIRMATION_STEPS;

  const hasTapStep = steps.some((s) => s.show_tap_button);
  const firstTapStepIndex = steps.findIndex((s) => s.show_tap_button);

  const resolveBody = (body: string) =>
    body
      .replace(/\{numVideos\}/gi, String(numVideos))
      .replace(/\{productLabel\}/gi, productLabel)
      .replace(/\{brand\}/gi, brandName)
      .replace(/\{guaranteed\}/gi, `$${guaranteed.toLocaleString()}`);

  return (
    <div className="bg-slate-100 rounded-lg p-6">
      <div className="max-w-md mx-auto space-y-3">
        <div className="text-center">
          <img src="/bmc-logo.png" alt="BMC" className="h-8 mx-auto" />
        </div>
        <div className="bg-white rounded-xl shadow-sm overflow-hidden">
          <div className="p-6">
            <div className="text-center mb-6">
              <div className="flex justify-center mb-3">
                <div className="rounded-full bg-green-100 p-3">
                  <CheckCircle2 className="h-8 w-8 text-green-600" />
                </div>
              </div>
              <p className="text-xl font-bold text-gray-900 mb-1">{acceptedTitle}</p>
              <p className="text-xs text-gray-500">{acceptedMessage}</p>
            </div>

            {hasTapStep && tapLink && (
              <div className="mb-4 bg-[#FB3AAE]/8 border border-[#FB3AAE]/25 rounded-xl px-3 py-2.5 flex items-start gap-2.5">
                <span className="text-base mt-0.5">👇</span>
                <div>
                  <p className="text-xs font-bold text-gray-900">You're not done yet!</p>
                  <p className="text-[10px] text-gray-600 mt-0.5">Complete Step {firstTapStepIndex + 1} below to request your free product sample before you're all set.</p>
                </div>
              </div>
            )}

            {steps.map((step, index) => (
              <div key={index}>
                <hr className="border-slate-200 mb-4" />
                <div className="mb-4">
                  <p className="text-[10px] font-bold text-gray-900 uppercase tracking-wider mb-2">
                    Step {index + 1} — {step.title}
                  </p>
                  {step.body && (
                    <p className="text-xs text-gray-600 leading-relaxed mb-2 whitespace-pre-wrap">
                      {resolveBody(step.body)}
                    </p>
                  )}
                  {step.show_tap_button && tapLink && (
                    <div className="w-full flex items-center justify-center text-sm font-bold text-white bg-[#FB3AAE] rounded-xl px-3 py-3 shadow-md shadow-pink-200">
                      Request Sample on TikTok →
                    </div>
                  )}
                </div>
              </div>
            ))}

            <hr className="border-slate-200 mb-4" />
            <div className="mb-4">
              <p className="text-[10px] font-bold text-gray-900 uppercase tracking-wider mb-2">Your Offer Recap</p>
              <div className="space-y-1.5 text-xs">
                <div className="flex justify-between">
                  <span className="text-gray-500">Guaranteed pay:</span>
                  <span className="font-medium text-gray-900">${guaranteed.toLocaleString()} (${values.offer_amount}/vid × {numVideos} videos)</span>
                </div>
                {hasRewards && firstPlaceAmount > 0 && (
                  <div className="flex justify-between">
                    <span className="text-gray-500">Prize pool:</span>
                    <span className="font-medium text-gray-900">up to ${firstPlaceAmount.toLocaleString()}</span>
                  </div>
                )}
                <div className="flex justify-between">
                  <span className="text-gray-500">TikTok Shop commission:</span>
                  <span className="font-medium text-gray-900">uncapped</span>
                </div>
                <div className="flex justify-between font-bold border-t border-slate-200 pt-1.5 mt-0.5">
                  <span className="text-gray-700">Potential total:</span>
                  <span className="text-[#FB3AAE]">${(guaranteed + (hasRewards ? firstPlaceAmount : 0)).toLocaleString()}+</span>
                </div>
              </div>
            </div>

            {hasRewards && (
              <>
                <hr className="border-slate-200 mb-4" />
                <div className="mb-4">
                  <p className="text-[10px] font-bold text-gray-900 uppercase tracking-wider mb-2">📊 Track Your Rankings</p>
                  <p className="text-xs text-gray-600 leading-relaxed mb-3">
                    Check the leaderboard anytime to see where you stand against other creators.
                  </p>
                  <div className="w-full flex items-center justify-center text-sm font-bold text-white bg-slate-800 rounded-xl px-3 py-2.5">
                    View Leaderboard →
                  </div>
                </div>
              </>
            )}

            <hr className="border-slate-200 mb-4" />
            <p className="text-xs text-center text-gray-400">
              {values.confirmation_footer || DEFAULT_CONFIRMATION_FOOTER}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

export function CampaignForm({ id }: CampaignFormProps) {
  const navigate = useNavigate();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const isEditing = !!id;
  const [showLandingPage, setShowLandingPage] = useState(true);
  const [previewTab, setPreviewTab] = useState<"offer" | "confirmation">("offer");
  const [selectedImageFile, setSelectedImageFile] = useState<File | null>(null);
  const [imagePreviewUrl, setImagePreviewUrl] = useState<string | null>(null);
  const [isDraggingOver, setIsDraggingOver] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const { data: campaign, isLoading } = useGetCampaign(id || "", {
    query: { enabled: isEditing, queryKey: getGetCampaignQueryKey(id || "") }
  });

  const form = useForm<CampaignFormValues>({
    resolver: zodResolver(campaignSchema),
    defaultValues: {
      brand_name: "",
      product_name: "",
      offer_amount: 0,
      num_videos_per_creator: 1,
      max_spots: undefined,
      brief_requirements: "",
      tap_link: "",
      has_rewards: false,
      prize_pool: [],
      product_id: "",
      campaign_start_date: "",
      campaign_end_date: "",
      additional_info: "",
      status: CampaignBodyStatus.active,
      accept_headline: "",
      accept_cta_text: "",
      accepted_title: "",
      accepted_message: "",
      confirmation_steps: DEFAULT_CONFIRMATION_STEPS,
      confirmation_footer: DEFAULT_CONFIRMATION_FOOTER,
      show_bonus_note: false,
      bonus_note_text: "",
      commission_text: "As a TikTok Shop affiliate, you earn commission on every sale your content drives — on top of your guaranteed pay.",
    },
  });

  const watchedValues = useWatch({ control: form.control }) as CampaignFormValues;

  useEffect(() => {
    if (campaign && isEditing) {
      const c = campaign as any;
      const hasLandingPageData = c.accept_headline || c.accept_cta_text || c.accepted_title || c.accepted_message || c.accepted_next_steps || c.show_bonus_note || c.bonus_note_text;
      if (hasLandingPageData) setShowLandingPage(true);
      if (c.product_image_url) {
        setImagePreviewUrl(c.product_image_url);
      }
      form.reset({
        brand_name: campaign.brand_name,
        product_name: campaign.product_name || "",
        offer_amount: campaign.offer_amount,
        num_videos_per_creator: campaign.num_videos_per_creator,
        max_spots: campaign.max_spots ?? undefined,
        brief_requirements: campaign.brief_requirements || "",
        tap_link: campaign.tap_link || "",
        has_rewards: c.is_rewards_campaign ?? c.has_rewards ?? false,
        prize_pool: c.prize_pool || [],
        product_id: Array.isArray(c.product_ids) ? (c.product_ids[0] || "") : "",
        campaign_start_date: c.campaign_start_date || "",
        campaign_end_date: c.campaign_end_date || "",
        additional_info: campaign.additional_info || "",
        status: campaign.status as CampaignBodyStatus,
        accept_headline: c.accept_headline || "",
        accept_cta_text: c.accept_cta_text || "",
        accepted_title: c.accepted_title || "",
        accepted_message: c.accepted_message || "",
        ...(() => {
          try {
            const parsed = c.accepted_next_steps ? JSON.parse(c.accepted_next_steps) : null;
            if (!parsed) return { confirmation_steps: DEFAULT_CONFIRMATION_STEPS, confirmation_footer: DEFAULT_CONFIRMATION_FOOTER };
            // New format: { steps, footer }
            if (!Array.isArray(parsed) && parsed.steps) {
              return {
                confirmation_steps: Array.isArray(parsed.steps) && parsed.steps.length > 0 ? parsed.steps : DEFAULT_CONFIRMATION_STEPS,
                confirmation_footer: parsed.footer || DEFAULT_CONFIRMATION_FOOTER,
              };
            }
            // Old format: bare array
            return {
              confirmation_steps: Array.isArray(parsed) && parsed.length > 0 ? parsed : DEFAULT_CONFIRMATION_STEPS,
              confirmation_footer: DEFAULT_CONFIRMATION_FOOTER,
            };
          } catch {
            return { confirmation_steps: DEFAULT_CONFIRMATION_STEPS, confirmation_footer: DEFAULT_CONFIRMATION_FOOTER };
          }
        })(),
        show_bonus_note: c.show_bonus_note || false,
        bonus_note_text: c.bonus_note_text || "",
        commission_text: c.commission_text || "As a TikTok Shop affiliate, you earn commission on every sale your content drives — on top of your guaranteed pay.",
      });
    }
  }, [campaign, isEditing, form]);

  const [isSavingDraft, setIsSavingDraft] = useState(false);
  const createMutation = useCreateCampaign();
  const updateMutation = useUpdateCampaign();

  const handleImageFile = useCallback((file: File) => {
    if (!["image/png", "image/jpeg", "image/webp"].includes(file.type)) {
      toast({ title: "Invalid file type", description: "Please select a PNG, JPG, or WebP image.", variant: "destructive" });
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast({ title: "File too large", description: "Please select an image under 5MB.", variant: "destructive" });
      return;
    }
    setSelectedImageFile(file);
    const url = URL.createObjectURL(file);
    setImagePreviewUrl(url);
  }, [toast]);

  const onSubmit = async (data: CampaignFormValues) => {
    const payload = {
      ...data,
      product_name: data.product_name || null,
      max_spots: data.max_spots,
      brief_requirements: data.brief_requirements || null,
      tap_link: data.tap_link || null,
      has_rewards: data.has_rewards ?? null,
      is_rewards_campaign: data.has_rewards ?? null,
      product_ids: data.has_rewards && data.product_id ? [data.product_id] : null,
      campaign_start_date: data.has_rewards && data.campaign_start_date ? data.campaign_start_date : null,
      campaign_end_date: data.has_rewards && data.campaign_end_date ? data.campaign_end_date : null,
      prize_pool: data.has_rewards && data.prize_pool && data.prize_pool.length > 0 ? data.prize_pool : null,
      additional_info: data.additional_info || null,
      accept_headline: data.accept_headline || null,
      accept_cta_text: data.accept_cta_text || null,
      accepted_title: data.accepted_title || null,
      accepted_message: data.accepted_message || null,
      accepted_next_steps: JSON.stringify({
        steps: data.confirmation_steps && data.confirmation_steps.length > 0 ? data.confirmation_steps : DEFAULT_CONFIRMATION_STEPS,
        footer: data.confirmation_footer || DEFAULT_CONFIRMATION_FOOTER,
      }),
      show_bonus_note: data.show_bonus_note ?? null,
      bonus_note_text: data.bonus_note_text || null,
    };

    try {
      let savedCampaign: Campaign;
      if (isEditing && id) {
        savedCampaign = await updateMutation.mutateAsync({ id, data: payload as unknown as CampaignBody });
        queryClient.invalidateQueries({ queryKey: getGetCampaignQueryKey(id) });
        logUpdate('hiring_campaigns', id, campaign as unknown as Record<string, any>, payload as Record<string, any>, { brand_name: data.brand_name });
      } else {
        savedCampaign = await createMutation.mutateAsync({ data: payload as unknown as CampaignBody });
        logCreate('hiring_campaigns', savedCampaign.id, payload as Record<string, any>, { brand_name: data.brand_name });
      }

      if (selectedImageFile) {
        const formData = new FormData();
        formData.append("file", selectedImageFile);
        const res = await fetch(`/api/campaigns/${savedCampaign.id}/upload-image`, {
          method: "POST",
          body: formData,
        });
        if (!res.ok) {
          const err = await res.json().catch(() => ({}));
          toast({
            title: isEditing ? "Campaign updated" : "Campaign created",
            description: `Image upload failed: ${err.error || "unknown error"}`,
            variant: "destructive",
          });
          navigate(`/hiring/campaigns/${savedCampaign.id}`);
          return;
        }
      }

      toast({ title: isEditing ? "Campaign updated successfully" : "Campaign created successfully" });
      navigate(`/hiring/campaigns/${savedCampaign.id}`);
    } catch (error: unknown) {
      const msg = (error as { message?: string; error?: string })?.message
        || (error as { error?: string })?.error
        || "An error occurred";
      toast({
        title: isEditing ? "Failed to update campaign" : "Failed to create campaign",
        description: msg,
        variant: "destructive",
      });
    }
  };

  const handleSaveAsDraft = async () => {
    const data = form.getValues();
    if (!data.brand_name) {
      toast({ title: "Brand name required", description: "Please enter a brand name before saving as draft.", variant: "destructive" });
      return;
    }
    const payload = {
      ...data,
      offer_amount: Number(data.offer_amount) || 0,
      num_videos_per_creator: Number(data.num_videos_per_creator) || 1,
      max_spots: data.max_spots != null && data.max_spots !== "" as any ? Number(data.max_spots) : null,
      status: CampaignBodyStatus.draft,
      product_name: data.product_name || null,
      // max_spots: data.max_spots,
      brief_requirements: data.brief_requirements || null,
      tap_link: data.tap_link || null,
      has_rewards: data.has_rewards ?? null,
      is_rewards_campaign: data.has_rewards ?? null,
      product_ids: data.has_rewards && data.product_id ? [data.product_id] : null,
      campaign_start_date: data.has_rewards && data.campaign_start_date ? data.campaign_start_date : null,
      campaign_end_date: data.has_rewards && data.campaign_end_date ? data.campaign_end_date : null,
      prize_pool: data.has_rewards && data.prize_pool && data.prize_pool.length > 0 ? data.prize_pool : null,
      additional_info: data.additional_info || null,
      accept_headline: data.accept_headline || null,
      accept_cta_text: data.accept_cta_text || null,
      accepted_title: data.accepted_title || null,
      accepted_message: data.accepted_message || null,
      accepted_next_steps: JSON.stringify({
        steps: data.confirmation_steps && data.confirmation_steps.length > 0 ? data.confirmation_steps : DEFAULT_CONFIRMATION_STEPS,
        footer: data.confirmation_footer || DEFAULT_CONFIRMATION_FOOTER,
      }),
      show_bonus_note: data.show_bonus_note ?? null,
      bonus_note_text: data.bonus_note_text || null,
    };
    try {
      setIsSavingDraft(true);
      let savedCampaign: Campaign;
      if (isEditing && id) {
        savedCampaign = await updateMutation.mutateAsync({ id, data: payload as unknown as CampaignBody });
        queryClient.invalidateQueries({ queryKey: getGetCampaignQueryKey(id) });
        logUpdate('hiring_campaigns', id, campaign as unknown as Record<string, any>, payload as Record<string, any>, { brand_name: data.brand_name, saved_as: 'draft' });
      } else {
        savedCampaign = await createMutation.mutateAsync({ data: payload as unknown as CampaignBody });
        logCreate('hiring_campaigns', savedCampaign.id, payload as Record<string, any>, { brand_name: data.brand_name, saved_as: 'draft' });
      }
      toast({ title: "Draft saved" });
      navigate(`/hiring/campaigns/${savedCampaign.id}`);
    } catch (error: unknown) {
      const msg = (error as { message?: string; error?: string })?.message
        || (error as { error?: string })?.error
        || "An error occurred";
      toast({ title: "Failed to save draft", description: msg, variant: "destructive" });
    } finally {
      setIsSavingDraft(false);
    }
  };

  const isPending = createMutation.isPending || updateMutation.isPending;

  if (isEditing && isLoading) {
    return <div className="flex justify-center p-8"><Loader2 className="h-8 w-8 animate-spin text-muted-foreground" /></div>;
  }

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-8 max-w-5xl">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-3xl">
          <div className="space-y-6">
            <FormField
              control={form.control}
              name="brand_name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Brand Name *</FormLabel>
                  <FormControl>
                    <Input placeholder="e.g. Acme Corp" {...field} data-testid="input-brand-name" />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="product_name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Product Name</FormLabel>
                  <FormControl>
                    <Input placeholder="e.g. Super Widget" {...field} data-testid="input-product-name" />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="space-y-2">
              <label className="text-sm font-medium">Product Image</label>
              <div
                className={`relative border-2 border-dashed rounded-xl transition-colors cursor-pointer ${
                  isDraggingOver
                    ? "border-[#FB3AAE] bg-pink-50"
                    : imagePreviewUrl
                    ? "border-slate-200 bg-slate-50"
                    : "border-slate-300 bg-slate-50 hover:border-slate-400"
                }`}
                onClick={() => fileInputRef.current?.click()}
                onDragOver={(e) => { e.preventDefault(); setIsDraggingOver(true); }}
                onDragLeave={() => setIsDraggingOver(false)}
                onDrop={(e) => {
                  e.preventDefault();
                  setIsDraggingOver(false);
                  const file = e.dataTransfer.files[0];
                  if (file) handleImageFile(file);
                }}
              >
                {imagePreviewUrl ? (
                  <div className="relative">
                    <img
                      src={imagePreviewUrl}
                      alt="Product preview"
                      className="w-full h-40 object-cover rounded-xl"
                    />
                    <button
                      type="button"
                      className="absolute top-2 right-2 bg-white/90 hover:bg-white rounded-full p-1 shadow-sm border border-slate-200"
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedImageFile(null);
                        setImagePreviewUrl(null);
                        if (fileInputRef.current) fileInputRef.current.value = "";
                      }}
                    >
                      <X className="h-3.5 w-3.5 text-slate-600" />
                    </button>
                    <div className="absolute bottom-2 left-2 bg-white/90 rounded-md px-2 py-0.5 text-[10px] text-slate-600 font-medium shadow-sm border border-slate-200">
                      Click to replace
                    </div>
                  </div>
                ) : (
                  <div className="flex flex-col items-center justify-center py-8 text-center px-4">
                    <div className="rounded-full bg-slate-100 p-3 mb-3">
                      <ImageIcon className="h-5 w-5 text-slate-400" />
                    </div>
                    <p className="text-sm font-medium text-slate-700 mb-1">Drop image here or click to upload</p>
                    <p className="text-xs text-slate-400">PNG, JPG, WebP — max 5MB</p>
                  </div>
                )}
              </div>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/png,image/jpeg,image/webp"
                className="hidden"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) handleImageFile(file);
                }}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="offer_amount"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Offer Amount ($/video) *</FormLabel>
                    <FormControl>
                      <Input type="number" {...field} data-testid="input-offer-amount" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="num_videos_per_creator"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Videos per Creator *</FormLabel>
                    <FormControl>
                      <Input type="number" {...field} data-testid="input-num-videos" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <FormField
              control={form.control}
              name="max_spots"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Target number of creators in the campaign *</FormLabel>
                  <FormControl>
                    <Input type="number" {...field} value={field.value || ''} data-testid="input-max-spots" />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            
            <FormField
              control={form.control}
              name="status"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Status</FormLabel>
                  <Select onValueChange={field.onChange} defaultValue={field.value} value={field.value}>
                    <FormControl>
                      <SelectTrigger data-testid="select-status">
                        <SelectValue placeholder="Select a status" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value={CampaignBodyStatus.draft}>Draft</SelectItem>
                      <SelectItem value={CampaignBodyStatus.active}>Active</SelectItem>
                      <SelectItem value={CampaignBodyStatus.paused}>Paused</SelectItem>
                      <SelectItem value={CampaignBodyStatus.completed}>Completed</SelectItem>
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          <div className="space-y-6">
            <FormField
              control={form.control}
              name="tap_link"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>TAP Link URL</FormLabel>
                  <FormControl>
                    <Input placeholder="https://..." {...field} data-testid="input-tap-link" />
                  </FormControl>
                  <FormDescription>Where creators request their sample.</FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="border rounded-lg p-4 space-y-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Trophy className="h-4 w-4 text-yellow-600" />
                  <span className="text-sm font-medium">Rewards Campaign</span>
                </div>
                <button
                  type="button"
                  onClick={() => {
                    const newVal = !watchedValues.has_rewards;
                    form.setValue("has_rewards", newVal);
                    if (newVal && (!watchedValues.prize_pool || watchedValues.prize_pool.length === 0)) {
                      form.setValue("prize_pool", DEFAULT_PRIZE_POOL);
                    }
                  }}
                  className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${watchedValues.has_rewards ? "bg-[#FB3AAE]" : "bg-gray-300"}`}
                >
                  <span className={`inline-block h-3.5 w-3.5 rounded-full bg-white transition-transform ${watchedValues.has_rewards ? "translate-x-4" : "translate-x-0.5"}`} />
                </button>
              </div>

              {watchedValues.has_rewards && (
                <>
                  <p className="text-xs text-muted-foreground">Set up the prize pool and campaign details for top-performing creators.</p>

                  <FormField
                    control={form.control}
                    name="product_id"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>TikTok Product ID</FormLabel>
                        <FormControl>
                          <Input placeholder="e.g. 1729641563832..." {...field} />
                        </FormControl>
                        <FormDescription>The TikTok Shop product ID for sales tracking.</FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <div className="grid grid-cols-2 gap-4">
                    <FormField
                      control={form.control}
                      name="campaign_start_date"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Campaign Start Date</FormLabel>
                          <FormControl>
                            <Input type="date" {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="campaign_end_date"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Campaign End Date</FormLabel>
                          <FormControl>
                            <Input type="date" {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>

                  <PrizePoolEditor
                    prizes={(watchedValues.prize_pool || DEFAULT_PRIZE_POOL) as unknown as { place: number; amount: number }[]}
                    onChange={(p) => form.setValue("prize_pool", p)}
                  />
                </>
              )}
            </div>
          </div>
        </div>

        <div className="border rounded-lg">
          <button
            type="button"
            className="w-full flex items-center justify-between px-4 py-3 text-sm font-medium text-left hover:bg-muted/50 transition-colors"
            onClick={() => setShowLandingPage(!showLandingPage)}
          >
            <span>Landing Page Customization</span>
            {showLandingPage ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
          </button>

          {showLandingPage && (
            <div className="border-t">
              <div className="flex border-b">
                <button
                  type="button"
                  className={`flex-1 px-4 py-2.5 text-sm font-medium transition-colors ${previewTab === "offer" ? "border-b-2 border-[#FB3AAE] text-[#FB3AAE]" : "text-muted-foreground hover:text-foreground"}`}
                  onClick={() => setPreviewTab("offer")}
                >
                  Offer Page
                </button>
                <button
                  type="button"
                  className={`flex-1 px-4 py-2.5 text-sm font-medium transition-colors ${previewTab === "confirmation" ? "border-b-2 border-[#FB3AAE] text-[#FB3AAE]" : "text-muted-foreground hover:text-foreground"}`}
                  onClick={() => setPreviewTab("confirmation")}
                >
                  Confirmation Template
                </button>
              </div>

              <div className="p-4">
                <div className="grid grid-cols-2 gap-6 items-start">
                  <div>
                    <p className="text-[11px] font-semibold text-slate-400 uppercase tracking-wider mb-2 text-center">Customize</p>
                    {previewTab === "offer" ? (
                      <OfferPageEditor form={form} values={watchedValues} />
                    ) : (
                      <ConfirmationPageEditor form={form} values={watchedValues} />
                    )}
                  </div>
                  <div>
                    <p className="text-[11px] font-semibold text-slate-400 uppercase tracking-wider mb-2 text-center">Live Preview</p>
                    {previewTab === "offer" ? (
                      <OfferPagePreview values={watchedValues} />
                    ) : (
                      <ConfirmationPagePreview values={watchedValues} />
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="flex justify-end gap-4 border-t pt-6">
          <Button
            type="button"
            variant="outline"
            onClick={() => navigate(isEditing ? `/hiring/campaigns/${id}` : "/hiring")}
            data-testid="btn-cancel"
          >
            Cancel
          </Button>
          <Button
            type="button"
            variant="outline"
            disabled={isPending || isSavingDraft}
            onClick={handleSaveAsDraft}
            data-testid="btn-save-draft"
          >
            {isSavingDraft && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Save as Draft
          </Button>
          <Button
            type="submit"
            disabled={isPending || isSavingDraft}
            data-testid="btn-submit"
          >
            {isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {isEditing ? "Save Changes" : "Create Campaign"}
          </Button>
        </div>
      </form>
    </Form>
  );
}
